import { useEffect, useRef, useSyncExternalStore } from 'react';
import {
  getStorageConsent, subscribeStorageConsent, isStorageSettingsOpen, openStorageSettings,
  closeStorageSettings, StorageChoice,
} from '../storageConsent';
import { applyStorageChoice } from '../state';
import { consentText } from '../i18n.consent';
import './storageConsent.css';

type Locale = 'de' | 'en';

/**
 * Site-wide cookie & storage banner. Used on the portfolio page and in the calculator; the
 * choice is shared because all pages use the same browser storage.
 *
 * Shown until the visitor decides, and again when reopened via <StorageSettingsLink />.
 * Both choices are equally prominent, and every page works the same either way.
 */
export function StorageConsentBanner({ locale }: { locale: Locale }) {
  const choice = useSyncExternalStore(subscribeStorageConsent, getStorageConsent, () => null);
  const reopened = useSyncExternalStore(subscribeStorageConsent, isStorageSettingsOpen, () => false);
  const visible = choice === null || reopened;
  const firstButton = useRef<HTMLButtonElement>(null);
  const tx = (key: Parameters<typeof consentText>[1]) => consentText(locale, key);

  // Move focus into the banner when it is reopened from the settings link.
  useEffect(() => {
    if (reopened) firstButton.current?.focus();
  }, [reopened]);

  if (!visible) return null;

  const decide = (next: StorageChoice) => {
    applyStorageChoice(next);
    closeStorageSettings();
  };

  return (
    <div className="consent-banner" role="dialog" aria-modal="false" aria-labelledby="consent-title" lang={locale}>
      <div className="consent-inner">
        <div className="consent-text">
          <h2 id="consent-title">{tx('consent.title')}</h2>
          <p>{tx('consent.body')}</p>
          {choice !== null && (
            <p className="consent-current">
              {choice === 'granted' ? tx('consent.currentGranted') : tx('consent.currentDenied')}
            </p>
          )}
          <p className="consent-hint">{tx('consent.hint')}</p>
        </div>
        <div className="consent-actions">
          <button ref={firstButton} type="button" className="consent-btn" onClick={() => decide('granted')}>
            {tx('consent.allow')}
          </button>
          <button type="button" className="consent-btn" onClick={() => decide('denied')}>
            {tx('consent.deny')}
          </button>
          {choice !== null && (
            <button type="button" className="consent-link" onClick={closeStorageSettings}>
              {tx('consent.close')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** "Cookie settings" link for a page footer; reopens the banner. */
export function StorageSettingsLink({ locale }: { locale: Locale }) {
  return (
    <button type="button" className="consent-link" id="btn-cookie-settings" onClick={openStorageSettings}>
      {consentText(locale, 'consent.settings')}
    </button>
  );
}

/** Language for pages without their own language switch: German for German browsers, else English. */
export function browserLocale(): Locale {
  return typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('de') ? 'de' : 'en';
}
